/* =============================================================================
 * M.M.LaFleur — collection infinite scroll.
 *
 * Loads the next page as the foot of the grid comes into view, and turns the
 * Load More link into an in-place loader rather than a navigation.
 *
 * PROGRESSIVE ENHANCEMENT, NOT REPLACEMENT
 * ----------------------------------------
 * mm_collection.liquid still renders the paginated grid and the Load More
 * link, and both stay in place. The link is the sentinel the observer watches,
 * the manual control, and — crucially — still a real <a href> in the served
 * HTML, so the collection remains fully walkable with JavaScript off and by
 * crawlers.
 *
 * WHY THE HREF IS REMOVED AT RUNTIME
 * ----------------------------------
 * The first version relied on preventDefault() from a capture-phase listener
 * to stop the link navigating. It did not hold — clicking Load More still
 * reloaded the whole page — which means something upstream was consuming the
 * click before this handler ran. Listener order at document level is not
 * something this file can guarantee: Platter binds in <head>, Barba binds its
 * own link handling, and both run before a footer script.
 *
 * So rather than race for the event, the link is disarmed. Once JS is running,
 * the href is moved to data-mm-next and the attribute removed, which leaves
 * nothing for the browser to navigate to no matter which handler wins. The
 * element stays an <a> so the existing .mm-btn styling still applies, and
 * gains the button role and keyboard handling it now needs.
 *
 * Because the swap only happens when this script executes, the no-JS path is
 * untouched — the server still sends a working link.
 *
 * ACROSS-PAGE DEDUPING
 * --------------------
 * mm_collection.liquid collapses colourways of one style into a single card,
 * but per render: its `seen` list resets on every page. A style whose colours
 * straddle a page boundary is therefore emitted again on the next page, so
 * cards are matched on product URL and repeats dropped.
 * ========================================================================== */

(function () {
  "use strict";

  /* Platter re-injects the footer's scripts around a Barba container swap, so
     without this the observer and listeners would be bound again on every soft
     navigation. init() is re-run from the hooks below instead. */
  if (window.__mmInfiniteScrollBound) return;
  window.__mmInfiniteScrollBound = true;

  /* How early to start fetching. Roughly one viewport of runway, so the next
     batch is usually in place before the shopper reaches the end. */
  var ROOT_MARGIN = "800px 0px";

  var loading = false;
  var observer = null;
  var watched = null;
  var pending = null;

  function grid() {
    return document.querySelector(".mm-plp-main .mm-product-grid") ||
           document.querySelector(".mm-product-grid");
  }

  function foot() {
    return document.querySelector(".mm-plp-foot");
  }

  /* The trigger, in whichever state it is currently in: already disarmed, or
     still the server-rendered anchor. */
  function trigger() {
    var f = foot();
    if (!f) return null;
    return f.querySelector("[data-mm-next]") || f.querySelector("a[href]");
  }

  function nextUrl() {
    var t = trigger();
    if (!t) return "";
    return t.getAttribute("data-mm-next") || t.getAttribute("href") || "";
  }

  /* Move the href out of the browser's reach. Done on every init and after
     every append, since the foot is replaced wholesale with server markup that
     arrives armed again. */
  function disarm() {
    var f = foot();
    if (!f) return;

    var link = f.querySelector("a[href]");
    if (!link) return;

    var url = link.getAttribute("href");
    if (!url) return;

    link.setAttribute("data-mm-next", url);
    link.removeAttribute("href");
    link.setAttribute("role", "button");
    link.setAttribute("tabindex", "0");
    /* An <a> with no href loses the pointer cursor and the focus ring it had
       as a link, so both are restored explicitly. */
    link.style.cursor = "pointer";
  }

  /* The product URL a card points at, used as its identity for deduping. */
  function cardKey(node) {
    var link = node.matches && node.matches("a[href]") ? node : node.querySelector("a[href]");
    if (!link) return "";
    return (link.getAttribute("href") || "").split("?")[0];
  }

  function existingKeys(container) {
    var keys = {};
    Array.prototype.forEach.call(container.children, function (child) {
      var k = cardKey(child);
      if (k) keys[k] = true;
    });
    return keys;
  }

  function setBusy(busy) {
    var t = trigger();
    if (!t) return;
    var label = t.querySelector(".mm-eyebrow");
    if (!label) return;
    if (busy) {
      if (!t.getAttribute("data-mm-label")) {
        t.setAttribute("data-mm-label", label.textContent);
      }
      label.textContent = "Loading…";
      t.setAttribute("aria-busy", "true");
    } else {
      var original = t.getAttribute("data-mm-label");
      if (original) label.textContent = original;
      t.removeAttribute("aria-busy");
    }
  }

  function load() {
    if (loading) return;

    var url = nextUrl();
    var container = grid();
    if (!url || !container) return;

    loading = true;
    setBusy(true);

    fetch(url, { headers: { "X-Requested-With": "fetch" } })
      .then(function (r) {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var incoming = doc.querySelector(".mm-plp-main .mm-product-grid") ||
                       doc.querySelector(".mm-product-grid");
        var incomingFoot = doc.querySelector(".mm-plp-foot");

        if (!incoming) throw new Error("no grid");

        var seen = existingKeys(container);
        var added = 0;

        while (incoming.firstElementChild) {
          var card = incoming.firstElementChild;
          incoming.removeChild(card);
          var key = cardKey(card);
          if (key && seen[key]) continue;
          if (key) seen[key] = true;
          container.appendChild(card);
          added++;
        }

        var currentFoot = foot();
        if (currentFoot) {
          if (incomingFoot) {
            /* Carries the updated "You've seen X of Y" count and the next
               page's link, or no link at all once the collection is
               exhausted. It arrives armed, hence the disarm below. */
            currentFoot.innerHTML = incomingFoot.innerHTML;
          } else {
            currentFoot.remove();
          }
        }

        loading = false;
        disarm();

        /* Reveal animations, lazy media and the card carousels all bind in
           mmInit, which is written to be idempotent and re-runnable. */
        if (typeof window.mmInit === "function") window.mmInit();

        watch();

        /* A page that contributed nothing after deduping would otherwise stall
           the scroll — the sentinel is already on screen and will not fire a
           fresh intersection. Keep going until something lands. */
        if (added === 0 && nextUrl()) load();
      })
      .catch(function () {
        loading = false;
        setBusy(false);
      });
  }

  function watch() {
    var f = foot();

    if (!f || !nextUrl()) {
      if (observer && watched) observer.unobserve(watched);
      watched = null;
      return;
    }

    if (!observer) {
      if (!("IntersectionObserver" in window)) return;
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) load();
        });
      }, { rootMargin: ROOT_MARGIN });
    }

    if (watched === f) return;
    if (watched) observer.unobserve(watched);
    watched = f;
    observer.observe(f);
  }

  function init() {
    pending = null;
    loading = false;
    disarm();
    watch();
  }

  function schedule() {
    if (pending) return;
    pending = window.requestAnimationFrame(init);
  }

  function isTrigger(target) {
    if (!target || !target.closest) return null;
    var f = target.closest(".mm-plp-foot");
    if (!f) return null;
    return target.closest("[data-mm-next]") || target.closest("a");
  }

  /* Bound on window rather than document, and on the capture phase, so this is
     the earliest point in the event path. Belt and braces alongside the
     disarming above — with no href there is nothing to navigate to, but
     stopping the event here also keeps Barba from treating it as a link. */
  window.addEventListener(
    "click",
    function (event) {
      if (!isTrigger(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      load();
    },
    true
  );

  /* An <a> without an href is no longer activated by Enter, and a role=button
     element is expected to respond to Space as well. */
  window.addEventListener(
    "keydown",
    function (event) {
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      if (!isTrigger(event.target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      load();
    },
    true
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", schedule);
  window.addEventListener("popstate", schedule);

  /* Barba replaces the main container without firing an event this script can
     see, so watch the DOM — the same approach mm-header-state.js uses. */
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
}());
